/**
 * What the page header says for each route: breadcrumb, title, one line of
 * description.
 *
 * Its own module because both the shell (which renders the header) and the
 * route tree (which defines the paths) need it, and importing one from the
 * other would close a cycle.
 *
 * Longest prefix wins, so a nested path inherits nothing by accident.
 */
export type PageMeta = {
  crumb: string;
  title: string;
  desc: string;
};

const PAGES: Array<[string, PageMeta]> = [
  ["/models", { crumb: "Settings", title: "모델 카탈로그", desc: "SAMS가 원본인 모델·가격을 관리하고, 환경마다 Base dev → prod로 배포한다." }],
  ["/call-sites", { crumb: "Settings", title: "호출 지점", desc: "Base와 SAMS가 모델을 부르는 곳마다 어떤 모델을 어떤 순서로 쓰는지 보고 고친다." }],
  ["/changelog", { crumb: "Settings", title: "변경 기록", desc: "SAMS 감사 기록의 전체 이력을 조회한다." }],
  ["/test-cases/candidates", { crumb: "Test Cases", title: "문제 식별", desc: "신호 탐지 › 정밀 탐지 › TC 단계마다 무엇을 왜 그렇게 판단했는지 보고, 그 단계를 다시 실행한다." }],
  [
    "/dashboard",
    {
      crumb: "Overview",
      title: "Dashboard",
      desc: "전체 테스트 현황과 점수 추이.",
    },
  ],
  [
    "/agents",
    {
      crumb: "Agent",
      title: "Synapse Agents",
      desc: "에이전트 카탈로그 · 프롬프트 · 스킬을 관리한다.",
    },
  ],
  [
    "/prompt-lab",
    {
      crumb: "Agent",
      title: "Prompt Lab",
      desc: "DB 에 올라가 있는 활성 프롬프트. 브라우저 Ctrl+F 로 본문 전체를 찾을 수 있다.",
    },
  ],
  [
    "/test-cases/all",
    {
      crumb: "Test Cases",
      title: "All Test Cases",
      desc: "👎 를 누른 쿼리만 TC 로 승격된다. 여기서는 분류하고 조회한다 — 실행은 하지 않는다.",
    },
  ],
  [
    "/test-cases/batch",
    {
      crumb: "Test Cases",
      title: "Batch Test",
      desc: "TC 를 고정하고 TE 를 하나씩 실행한다. 단일 TC 1 개, 또는 2 개 이상.",
    },
  ],
  [
    "/test-cases/result",
    {
      crumb: "Test Cases",
      title: "Test Result",
      desc: "실행 결과를 TC × TE Matrix 로 본다. 셀 = Final 점수(0~1, rubric v2).",
    },
  ],
  [
    "/test-cases/references",
    {
      crumb: "Test Cases",
      title: "Reference Answer",
      desc: "TC 를 골라 Reference Answer 를 새로 만들고, 만든 답을 사람이 검토한다. 검토에서 통과한 답만 채점 기준이 된다.",
    },
  ],
  [
    "/test-cases/cause-loop",
    {
      crumb: "Test Cases",
      title: "루프 대상",
      desc: "활성이고 Reference 를 사람이 통과시킨 TC 를 점수 차이(Reference v3 − TC 답 v3) 순으로 본다. 루프 실행은 MCP·CLI 로 한다.",
    },
  ],
  [
    "/conversations",
    {
      crumb: "Evaluation",
      title: "Conversations",
      desc: "세션을 검색하거나 session_id 로 대화를 열고, 답변의 Trace 를 본다. 👎 여부와 무관하게 실행된 모든 대화가 대상이다.",
    },
  ],
  [
    "/proactive-messages",
    {
      crumb: "Engagement",
      title: "Proactive Messages",
      desc: "선제 메시지 발송 로그와 반응.",
    },
  ],
  [
    "/home-feed",
    {
      crumb: "Engagement",
      title: "Home Feed",
      desc: "어떤 카드가 누구에게 생성됐고, 본 뒤 무엇을 했는지.",
    },
  ],
  [
    "/admins",
    {
      crumb: "Settings",
      title: "SAMS Admins",
      desc: "SAMS 를 쓸 수 있는 관리자와 prod 권한을 관리하고, 모든 변경 이력을 본다.",
    },
  ],
  ["/backend-errors", {
    crumb: "Evaluation", title: "Backend Errors",
    desc: "dev·prod·SAMS에 기록된 도구 호출 실패와 그때의 요청 조건, 제공자 오류 응답을 확인한다.",
  }],
];

const FALLBACK: PageMeta = {
  crumb: "SAMS",
  title: "찾을 수 없음",
  desc: "이 주소에 해당하는 화면이 없습니다.",
};

export function pageMetaFor(pathname: string): PageMeta {
  let best: PageMeta | null = null;
  let bestLength = -1;
  for (const [prefix, meta] of PAGES) {
    if (pathname.startsWith(prefix) && prefix.length > bestLength) {
      best = meta;
      bestLength = prefix.length;
    }
  }
  return best ?? FALLBACK;
}
